import React, { FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, LinearProgress, Paper, TextField } from '@mui/material';
import AiAlert from 'components/shared/ai-alert/ai-alert.component';
import { useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { aiRestorationsTemplate } from 'apollo/templates/order';
import AiCreateConstructionDialog from '../../../shared/constructions/construction-ai/ai-create-construction-dialog.component';
import { SimpleOrderContext } from 'pages/order-flow/simple-order/simple-order-context';
import { ConstructionProperty } from 'models/property';
import { OrderFormRestorationJaw, RestorationData } from 'models/order-form';
import { CONSTANTS } from 'config/constants';
import { filterTeethByRange } from 'components/order-flow/shared/constructions-helper';
import { SimpleSubCategory } from 'models/order-input/simple-order';
import { RestorationInput } from 'models/order-input/restoration-input';
import { TeethRange } from 'models/material';
import { cateogryMap, materialMap } from 'components/order-flow/shared/data-mapping';

const { UPPER_JAW, LOWER_JAW } = CONSTANTS;

const CreateConstructionAi: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const { order, setOrder } = useContext(SimpleOrderContext);
    const { showBoundary } = useDenthubErrorBoundary();
    const [promt, setPromt] = useState('');
    const [isCreteAiDialogOpen, setIsCreteAiDialogOpen] = useState(false);
    const [foundNoAiRestorations, setFoundNoAiRestorations] = useState(false);
    const [teeth, setTeeth] = useState<number[]>([]);
    const [teethRange, setTeethRange] = useState<TeethRange>();
    const [settings, setSettings] = useState<ConstructionProperty>({} as ConstructionProperty);
    const [currentCateogry, setCurrentCateogry] = useState<SimpleSubCategory>(SimpleSubCategory.SINGLE_CROWN);

    const [getAiRestorations, { loading }] = useLazyQuery(aiRestorationsTemplate, {
        onCompleted({ aiRestorations }: { aiRestorations: RestorationData[] }) {
            if (aiRestorations.length > 0) {
                const restoration = aiRestorations[0];
                if (restoration.teeth && restoration.teeth.length > 0) {
                    setTeeth(restoration.teeth.map((t: string) => parseInt(t)));
                } else if (restoration.teethRange && restoration.teethRange.length > 0) {
                    const teethRange = restoration.teethRange[0];
                    const from = parseInt(teethRange.from);
                    const to = parseInt(teethRange.to);
                    setTeethRange({ start: from, end: to });
                } else if (restoration.jaw) {
                    if (restoration.jaw === OrderFormRestorationJaw.UPPER_JAW) {
                        setTeeth(UPPER_JAW.map((t) => parseInt(t)));
                    } else if (restoration.jaw === OrderFormRestorationJaw.LOWER_JAW) {
                        setTeeth(LOWER_JAW.map((t) => parseInt(t)));
                    } else if (restoration.jaw === OrderFormRestorationJaw.BOTH_JAWS) {
                        setTeeth([...UPPER_JAW.map((t) => parseInt(t)), ...LOWER_JAW.map((t) => parseInt(t))]);
                    }
                }
                setSettings({
                    material: materialMap[restoration.material || ''],
                    color: restoration.color || '',
                    shade: 'Vita Classic',
                } as ConstructionProperty);
                setCurrentCateogry(cateogryMap[restoration.restorationType]);
                setIsCreteAiDialogOpen(true);
            } else {
                setFoundNoAiRestorations(true);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const hadleAddConstructionClick = () => {
        setFoundNoAiRestorations(false);
        if (promt && promt !== '') {
            getAiRestorations({ variables: { promt } });
        }
    };

    const handleCreatedAiRestoration = (restoration: RestorationInput) => {
        setOrder((prev) => ({
            ...prev,
            restorations: [...prev.restorations, restoration],
            comment: prev.comment ? prev.comment + `\n${promt}` : promt,
        }));
        setPromt('');
        setIsCreteAiDialogOpen(false);
    };

    const getAiMessage = () => {
        if (loading) {
            return translate('orders:CREATE_CONSTRUCTION_WITH_AI_LOADING');
        } else if (foundNoAiRestorations) {
            return translate('orders:CREATE_CONSTRUCTION_WITH_AI_NOT_FOUND');
        }
        return translate('orders:CREATE_CONSTRUCTION_WITH_AI_TITLE');
    };

    return (
        <Fragment>
            <Paper variant="outlined">
                <Box p={2}>
                    <Box mb={1}>
                        <AiAlert message={getAiMessage()} />
                    </Box>
                    <Box mb={1}>
                        <TextField
                            multiline
                            minRows={2}
                            value={promt}
                            onChange={(e) => setPromt(e.target.value)}
                            placeholder={translate('orders:CREATE_CONSTRUCTION_WITH_AI_PLACEHOLDER')}
                            fullWidth
                            data-test="create-construction-ai.input"
                        />
                    </Box>
                    {loading ? (
                        <LinearProgress />
                    ) : (
                        <Button
                            disabled={!promt}
                            onClick={hadleAddConstructionClick}
                            data-test="create-construction-ai.add-btn"
                        >
                            {translate('orders:ADD_CONSTRUCTION_WITH_AI')}
                        </Button>
                    )}
                </Box>
            </Paper>
            <AiCreateConstructionDialog
                category={currentCateogry}
                open={isCreteAiDialogOpen}
                onClose={() => setIsCreteAiDialogOpen(false)}
                onAddRestoration={handleCreatedAiRestoration}
                teeth={teeth}
                teethRange={teethRange}
                settings={settings}
            />
        </Fragment>
    );
};

export default CreateConstructionAi;
